import { AgGridReact } from 'ag-grid-react'
import classNames from 'classnames'
import { forwardRef, memo, useCallback, useEffect } from 'react'
import { MdAdd } from 'react-icons/md'

import type { Props as TableProps } from '../Table'
import type { FilterChangedEvent, GridReadyEvent, SortChangedEvent } from 'ag-grid-community'
import type { GridOptions } from 'ag-grid-enterprise'
import type { ChangeEvent, ReactElement } from 'react'
import type { IPaginationApi } from '~/hooks/useTablePagination'

import { commonProps, sideBar } from '../common'

import Button from '~/components/Buttons/Button'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import Pagination from '~/components/Pagination'
import ShowItems from '~/components/ShowItems'
import { DEFAULT_SEARCH, DEFAULT_SORT } from '~/hooks/useTablePagination'
import { debounce } from '~/utils/function'
import { getOperatorFromGridFilter } from '~/utils/pagination'
import { getButtonWithTooltip } from '~/utils/table'

type Props = {
  currentPage?: number
  onSearchChange?: (event: ChangeEvent<HTMLInputElement>) => void
  paginationApi?: IPaginationApi
  searchField?: string
  total?: number
}

export type ServerSideTableProps = Props & TableProps & GridOptions

const ServerSideTable = forwardRef<AgGridReact, ServerSideTableProps>(
  (
    {
      addButton,
      addButtonDisabled,
      addButtonLabel,
      addButtonTooltip,
      buttonComponent,
      className,
      columnDefs,
      currentPage = 0,
      editControls,
      entriesName,
      isSideBar,
      nodeBeforeSearchBar,
      onAddButtonClick,
      onFilterChanged,
      onSearchChange,
      paginationApi,
      rowData,
      searchField,
      searchPlaceholder,
      tableRef,
      total = 0,
      withPagination = true,
      withSearchBar,
      withSearchBarAutoFocus,
      ...props
    },
    ref
  ): ReactElement => {
    /** Local state */

    const searchContainerClasses = classNames('search-container', { 'with-searchbar': withSearchBar })

    /** Effects */

    const sizeColumnsToFit = useCallback(() => {
      // @ts-expect-error current not defined
      ref?.current.api.sizeColumnsToFit()
    }, [ref])

    useEffect(() => {
      window.addEventListener('resize', sizeColumnsToFit)
      return () => {
        window.removeEventListener('resize', sizeColumnsToFit)
      }
    }, [sizeColumnsToFit])

    /** Handlers */

    const handleGridReady = useCallback((evt: GridReadyEvent) => {
      evt.api.sizeColumnsToFit()
    }, [])

    const handleGoToPage = (page: number) => paginationApi?.setPage(page)

    const handleGoBack = () => paginationApi?.setPage((currentPage) => currentPage - 1)

    const handleGoNext = () => paginationApi?.setPage((currentPage) => currentPage + 1)

    const handleSortChanged = (event: SortChangedEvent) => {
      if (paginationApi) {
        const colState = event.api.getColumnState()
        const sortState = colState.filter((s) => s.sort != null)

        paginationApi.setOrderBy(sortState.length ? `${sortState[0].colId} ${sortState[0].sort}` : DEFAULT_SORT)
      }
    }

    const handleSearchTextChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
      if (paginationApi) {
        const text = event.target.value
        paginationApi.setSearch(text ? { [`${searchField || 'name'}:like`]: text } : DEFAULT_SEARCH)
        paginationApi.setPage(0)
      }
    }, 500)

    const handleFilterChanged = (event: FilterChangedEvent) => {
      if (paginationApi) {
        const filterState = event.api.getFilterModel()
        const filters: Record<string, string | number> = {}

        Object.entries(filterState).forEach(([key, { filter, filterType, type, values }]) => {
          const formattedKey = key.replace('_', '-')
          switch (filterType) {
            case 'text':
              filters[`${formattedKey}:${getOperatorFromGridFilter(type)}`] = filter
              break
            case 'set':
              values.forEach((value: string) => {
                filters[`${formattedKey}:in`] = value
              })
              break

            default:
              return
          }
        })
        paginationApi.setFilters(filters)
        paginationApi.setPage(0)
      }
    }

    const handleItemPerPageChange = (size: number) => paginationApi?.setSize(size)

    return (
      <div className={classNames('table-component', className)}>
        {withSearchBar || addButton || withPagination ? (
          <div className={searchContainerClasses}>
            {nodeBeforeSearchBar}
            {withSearchBar ? (
              <div className="table-search-bar-container">
                <SearchInput
                  autoFocus={withSearchBarAutoFocus}
                  className="table-search-bar"
                  onChange={onSearchChange || handleSearchTextChange}
                  placeholder={searchPlaceholder}
                />
              </div>
            ) : null}
            {buttonComponent}
            {addButton ? (
              addButtonTooltip ? (
                getButtonWithTooltip(
                  addButtonTooltip,
                  <Button disabled={addButtonDisabled} onClick={onAddButtonClick}>
                    <MdAdd color="#fff" size={16} />
                    {addButtonLabel}
                  </Button>
                )
              ) : (
                <Button disabled={addButtonDisabled} onClick={onAddButtonClick}>
                  <MdAdd color="#fff" size={16} />
                  {addButtonLabel}
                </Button>
              )
            ) : null}
            {editControls ? <div className="edition-controls-wrapper">{editControls}</div> : null}
            {withPagination && paginationApi && rowData && rowData.length > 0 ? (
              <ShowItems
                className="table-show-items-component"
                entriesName={entriesName}
                maxNumberEntries={100}
                numberPerPage={paginationApi.size}
                setNumberPerPage={handleItemPerPageChange}
                totalNumberElements={total}
              />
            ) : null}
          </div>
        ) : null}
        <div className="table-wrapper ag-theme-alpine" ref={tableRef}>
          <AgGridReact
            {...commonProps}
            columnDefs={columnDefs}
            onFilterChanged={onFilterChanged || handleFilterChanged}
            onGridReady={handleGridReady}
            onSortChanged={handleSortChanged}
            paginationPageSize={paginationApi?.size}
            ref={ref}
            rowData={rowData}
            sideBar={isSideBar ? sideBar : undefined}
            {...props}
          />
        </div>
        {withPagination && paginationApi ? (
          <Pagination
            className="table-pagination"
            currentPage={currentPage}
            goBack={handleGoBack}
            goNext={handleGoNext}
            goToPage={handleGoToPage}
            pageTotal={Math.ceil(total / paginationApi.size)}
          />
        ) : null}
      </div>
    )
  }
)

ServerSideTable.displayName = 'ServerSideTable'

export default memo(ServerSideTable)
